<template>
  <div>Säkra meddelanden</div>
  <div class="type-body-sm mb-xs text-text-secondary">
    Svaren får innehålla personuppgifter. Kunden kan endast läsa dem när den
    loggar in med BankID.
  </div>
  <avtalat-select
    id="svarsmall"
    v-model:value="selectedSvarsmallId"
    label="Svarsmall"
    :labelHidden="true"
  >
    <option value="">Välj en svarsmall (Valfritt)</option>
    <optgroup
      v-for="(group, groupIndex) in Object.keys(
        svarsmallStore.getGroupedSvarsmallar,
      )"
      :key="groupIndex"
      :label="group"
    >
      <option
        v-for="mall in svarsmallStore.getGroupedSvarsmallar[group]"
        :key="mall.id"
        :value="mall.id"
      >
        {{ mall.name }}
      </option>
    </optgroup>
  </avtalat-select>
  <RichTextInput
    v-model="modelValue"
    placeholder="Skriv något till kunden"
    @ready="onInputReady"
  />
  <span
    class="font-bold text-[--feedback-alert-darker]"
    v-if="showEmailIsMissingWarning"
    >Kunden saknar e-postadress</span
  >
  <FormErrorMessage name="sakraMeddelanden" />
  <InlineConversation
    v-if="sakraMeddelanden.length"
    :sakraMeddelanden="sakraMeddelanden"
    :showVerified="false"
    heading="Säkra meddelanden"
  />
</template>

<script setup lang="ts">
import { type Ref, ref, watch } from "vue";

import InlineConversation from "@/components/arende-components/SakraMeddelandenList.vue";
import RichTextInput from "@/components/rich-text-input/RichTextInput.vue";
import FormErrorMessage from "@/components/ui/FormErrorMessage.vue";
import type { ISakertMeddelande } from "@/composables/KundserviceAppApiClient";
import {
  useIndividStore,
  useSvarsmallarStore,
  useUserProfileStore,
} from "@/stores";

const svarsmallStore = useSvarsmallarStore();
const individStore = useIndividStore();
const userProfileStore = useUserProfileStore();

// If template is unchanged, we want to emit "template-unchanged" with true, otherwise false.
// If the template is unchanged the user should not be able to forward (lämna över) the ticket to another agent,
// because the next agent might think that the customer
// has already seen the message, when in fact they haven't.
const emit = defineEmits<{
  "template-unchanged": [value: boolean];
}>();

const selectedSvarsmallId: Ref<string> = ref("");
const selectedTemplateNormalizedText = ref("");

const modelValue = defineModel<string>();

const normalizeRichText = (value?: string | null): string => {
  if (!value) {
    return "";
  }

  const parser = new DOMParser();
  const parsed = parser.parseFromString(value, "text/html");
  return (parsed.body.textContent ?? "")
    .replaceAll("\u00a0", " ")
    .replaceAll(/\s+/g, " ")
    .trim();
};

watch(
  selectedSvarsmallId,
  (newSvarsmallId: string | null, oldSvarsmallId: string | null) => {
    if (newSvarsmallId && newSvarsmallId !== oldSvarsmallId) {
      const selectedTemplate = svarsmallStore.getById(newSvarsmallId);
      const newText = replaceSvarsmallPlaceholders(
        selectedTemplate?.content ?? "",
      );
      modelValue.value = newText;
      selectedTemplateNormalizedText.value = normalizeRichText(newText);
      emit(
        "template-unchanged",
        selectedTemplate?.id === svarsmallStore.defaultFirstAnswerMall?.id,
      );
      return;
    }

    if (newSvarsmallId === "") {
      modelValue.value = "";
      selectedTemplateNormalizedText.value = "";
      emit("template-unchanged", false);
    }
  },
);

watch(
  modelValue,
  (newValue) => {
    if (!selectedSvarsmallId.value) {
      emit("template-unchanged", false);
      return;
    }

    const selectedTemplate = svarsmallStore.getById(selectedSvarsmallId.value);
    const shouldShowUnchangedDialog =
      selectedTemplate?.id === svarsmallStore.defaultFirstAnswerMall?.id;

    emit(
      "template-unchanged",
      shouldShowUnchangedDialog &&
      normalizeRichText(newValue) === selectedTemplateNormalizedText.value,
    );
  },
  { immediate: true },
);

const replaceSvarsmallPlaceholders = (svarsmallContent: string): string => {
  const customerFirstName =
    individStore.individ.data?.tilltalsnamn ||
    individStore.individ.data?.fornamn ||
    "";
  const agentFirstName = userProfileStore.givenName || "";

  return svarsmallContent
    .replace(/\{CustomerFirstName\}/g, customerFirstName)
    .replace(/\{AgentFirstName\}/g, agentFirstName);
};

const props = defineProps<{
  sakraMeddelanden: ISakertMeddelande[];
  showEmailIsMissingWarning: boolean;
  shouldUseDefaultTemplate: boolean;
}>();

const onInputReady = () => {
  if (props.shouldUseDefaultTemplate) {
    selectedSvarsmallId.value = svarsmallStore.defaultFirstAnswerMall?.id ?? "";
  }
};
</script>
